/**
 * 주문 기본설정 (고도몰 ID51_1 "주문 기본 설정"). be-order-lifecycle 계약.
 *
 * GET/PUT `/api/admin/v1/orders/policy-settings` (게이트웨이 `/admin/v1/orders/**`
 * RewritePath 재사용). v2 envelope `{ success, data, error, meta }`. PUT 은 전체폼 제출
 * + `Idempotency-Key`(재시도 안전).
 *
 * TASK-1 보강: V35 로 OrderPolicySettings 가 6→26 필드 확장(고도몰 01·02.png 정합).
 * BE `OrderPolicySettingsDtos` Request 26 / Response 28(+감사 메타). 운영자가 BO 에서
 * ON/OFF·일수·시점을 편집 → PUT 전송. 일부 신규 필드(시점 enum·클레임 복원 9종)는
 * **시드/표시·정책 저장**까지이며 실제 로직 적용은 후속 TASK(2/4/5).
 *
 * BE 기본값(고도몰 정본): autoConfirmDays=10 / unpaidAutoCancelDays=3 /
 * autoDeliveryComplete(true,30) / purchaseOfferReconfirm=true / refundProgressReconfirm=false /
 * claimRequest(true, mark=true, vendor=false) / autoRefundEnabled=false /
 * stockDecrementTiming=ORDER / benefitGrantTiming=PURCHASE_CONFIRM.
 * BE 가 일수 필드(paymentDueHours·autoConfirmDays·autoDeliveryCompleteDays·unpaidAutoCancelDays)를 ≥1 클램프.
 */

/** 재고 차감 시점(시드/표시 — 차감 로직 전환은 TASK-2). */
export const STOCK_DECREMENT_TIMINGS = ['ORDER', 'PAYMENT', 'DELIVERED'] as const;
export type StockDecrementTiming = (typeof STOCK_DECREMENT_TIMINGS)[number];

/** 혜택(마일리지·쿠폰) 지급 시점(시드/표시 — 지급 시점 전환은 TASK-4/5). */
export const BENEFIT_GRANT_TIMINGS = ['PAYMENT', 'PURCHASE_CONFIRM', 'DELIVERED'] as const;
export type BenefitGrantTiming = (typeof BENEFIT_GRANT_TIMINGS)[number];

/** PUT 요청 본문 = 응답 settings 본체와 동일(updatedBy/At 제외). */
export interface OrderPolicySettingsPayload {
  // --- 기존 6필드 (회귀 민감 — 동작 불변) ---
  /** 미결제 주문 결제 기한(시간). BE ≥1 클램프. */
  paymentDueHours: number;
  /** 결제 기한 경과 시 자동 취소 여부. */
  unpaidAutoCancelEnabled: boolean;
  /** 결제완료 시 자동 상품준비중(READY_FOR_FULFILLMENT) 전환 여부. */
  autoReadyOnPayment: boolean;
  /** 배송완료 후 자동 구매확정 활성화 여부. */
  autoConfirmEnabled: boolean;
  /** 배송완료 후 자동 구매확정까지의 일수(고도몰=10). BE ≥1 클램프. */
  autoConfirmDays: number;
  /** 품절 상품 주문 차단 여부. */
  soldoutOrderBlock: boolean;

  // --- TASK-1 신규 정책 11필드 ---
  /** 미입금 자동취소 일수(고도몰=3). BE ≥1 클램프. unpaidAutoCancelEnabled 와 짝. */
  unpaidAutoCancelDays: number;
  /** 자동 배송완료 사용 여부(배송중+N일→배송완료). */
  autoDeliveryCompleteEnabled: boolean;
  /** 자동 배송완료까지의 일수(고도몰=30). BE ≥1 클램프. */
  autoDeliveryCompleteDays: number;
  /** 결제페이지 청약의사 재확인 사용 여부(고도몰=사용함). */
  purchaseOfferReconfirm: boolean;
  /** 환불 진행 재확인 사용 여부(고도몰=사용안함). */
  refundProgressReconfirm: boolean;
  /** 교환/반품/환불 신청 사용 여부(고도몰=사용함, 고객 직접 신청). */
  claimRequestEnabled: boolean;
  /** 고객 신청 건 주문목록 표기 여부(고도몰=ON). */
  claimRequestOrderMark: boolean;
  /** 공급사 관리자 동일 적용 여부(고도몰=OFF). */
  claimRequestVendorApply: boolean;
  /** 자동환불 사용 여부(고도몰=사용안함, 전 환불 운영자 승인). */
  autoRefundEnabled: boolean;
  /** 재고 차감 시점(고도몰=주문 시 ORDER). 시드/표시(전환=TASK-2). */
  stockDecrementTiming: StockDecrementTiming;
  /** 혜택 지급 시점(고도몰=구매확정 PURCHASE_CONFIRM). 시드/표시(전환=TASK-4/5). */
  benefitGrantTiming: BenefitGrantTiming;

  // --- TASK-1 신규 클레임 복원 기본값 9필드 (정책 저장; 노출=클레임화면 TASK-2-3, 적용=TASK-5) ---
  /** 주문취소 시 재고 복원(고도몰=복원함). */
  cancelRestoreStock: boolean;
  /** 주문취소 시 쿠폰 복원(고도몰=복원안함). */
  cancelRestoreCoupon: boolean;
  /** 주문취소 시 사은품 지급(고도몰=지급함). */
  cancelGrantFreebie: boolean;
  /** 교환접수 시 교환취소 상품 쿠폰 복원(고도몰=복원안함). */
  exchangeRestoreCoupon: boolean;
  /** 교환접수 시 교환취소 상품 사은품 지급(고도몰=지급함). */
  exchangeGrantFreebie: boolean;
  /** 교환접수 시 교환추가 상품 마일리지 지급(고도몰=지급함). */
  exchangeGrantMileage: boolean;
  /** 교환접수 시 교환추가 상품 쿠폰마일리지 지급(고도몰=지급함). */
  exchangeGrantCouponMileage: boolean;
  /** 환불완료 시 재고 복원(고도몰=복원안함). */
  refundRestoreStock: boolean;
  /** 환불완료 시 쿠폰 복원(고도몰=복원안함). */
  refundRestoreCoupon: boolean;
}

/** GET/PUT 응답 = 설정 26필드 + 감사 메타. */
export interface OrderPolicySettings extends OrderPolicySettingsPayload {
  updatedBy?: string | null;
  updatedAt?: string | null;
}

/** 기본값(BE 미설정·로드 전 폼 시드 — 고도몰 정본). */
export const DEFAULT_ORDER_POLICY: OrderPolicySettings = {
  // 기존 6
  paymentDueHours: 72,
  unpaidAutoCancelEnabled: true,
  // F5(O-7): BE defaults()=false / R4-A QA 근거가 정본 — V32 seed=true 는 stale 아티팩트.
  // BE task #7(guarded 마이그레이션)과 동반 적용. 폼 시드 기본값을 BE 와 일치시킨다.
  autoReadyOnPayment: false,
  autoConfirmEnabled: true,
  autoConfirmDays: 10,
  soldoutOrderBlock: true,
  // 신규 정책 11
  unpaidAutoCancelDays: 3,
  autoDeliveryCompleteEnabled: true,
  autoDeliveryCompleteDays: 30,
  purchaseOfferReconfirm: true,
  refundProgressReconfirm: false,
  claimRequestEnabled: true,
  claimRequestOrderMark: true,
  claimRequestVendorApply: false,
  autoRefundEnabled: false,
  stockDecrementTiming: 'ORDER',
  benefitGrantTiming: 'PURCHASE_CONFIRM',
  // 신규 클레임 복원 9
  cancelRestoreStock: true,
  cancelRestoreCoupon: false,
  cancelGrantFreebie: true,
  exchangeRestoreCoupon: false,
  exchangeGrantFreebie: true,
  exchangeGrantMileage: true,
  exchangeGrantCouponMileage: true,
  refundRestoreStock: false,
  refundRestoreCoupon: false,
  // 감사 메타
  updatedBy: null,
  updatedAt: null,
};
